import React from 'react';
import { Copy, Check, Download, Share2, Bookmark } from 'lucide-react';
import { Button } from '../../mawi-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../mawi-ui';
import { cn } from '../../utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface EnhancedMessageProps {
  message: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp?: number;
  };
  onCopy?: (content: string) => void;
  onSave?: (message: any) => void;
  onShare?: (message: any) => void;
}

const EnhancedMessage: React.FC<EnhancedMessageProps> = ({
  message,
  onCopy,
  onSave,
  onShare,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [showActions, setShowActions] = React.useState(false);

  const handleCopy = async (format: 'text' | 'markdown' = 'text') => {
    let contentToCopy = message.content;

    if (format === 'markdown' && message.role === 'assistant') {
      // For markdown responses, copy the raw markdown
      contentToCopy = message.content;
    }

    try {
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.(contentToCopy);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = () => {
    onSave?.(message);
  };

  const handleShare = () => {
    onShare?.(message);
  };

  return (
    <div
      className={cn(
        "group relative p-4 rounded-lg transition-colors",
        message.role === 'user'
          ? 'bg-primary text-primary-foreground ml-12'
          : 'bg-muted mr-12 hover:bg-muted/80'
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Message Actions */}
      <div className={cn(
        "absolute top-2 right-2 flex gap-1 transition-opacity",
        showActions ? "opacity-100" : "opacity-0"
      )}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy('text')}
              className="h-6 w-6 p-0"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy as text</TooltipContent>
        </Tooltip>

        {message.role === 'assistant' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy('markdown')}
                className="h-6 w-6 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy as markdown</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              className="h-6 w-6 p-0"
            >
              <Bookmark className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Save message</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="h-6 w-6 p-0"
            >
              <Share2 className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Share message</TooltipContent>
        </Tooltip>
      </div>

      {/* Message Content */}
      <div className="pr-20">
        {message.role === 'user' ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="prose prose-sm dark:prose-invert max-w-none"
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>

      {/* Timestamp */}
      {message.timestamp && (
        <div className="text-xs opacity-60 mt-2">
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default EnhancedMessage;